import type {
  CollectionReference,
  DocumentReference,
  Firestore,
} from 'firebase/firestore'
import {
  collection,
  doc,
  query,
  serverTimestamp,
  Timestamp,
  where,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { DateTime } from 'luxon'
import {
  getDocWithError,
  runTransactionWithError,
} from '../../firestore-mobx/fetch'
import {
  convertDocumentSnapshotToModel,
  modelListStream,
} from '../../firestore-mobx/stream'
import { ClickWrap } from '../../models/ClickWrap'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { getColRef as getUpdateColRef } from '../ClickWrapUpdate'
import type { ClickWrapRole, FirestoreClickWrap } from './schema'
import { ClickWrapState, schema } from './schema'

const converter: FirestoreDataConverter<FirestoreClickWrap> = {
  toFirestore: (data) => {
    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (
  firestore: Firestore
): CollectionReference<FirestoreClickWrap> => {
  return collection(firestore, 'click_wrap').withConverter(converter)
}

const getDocRef = (
  firestore: Firestore,
  clickWrapId: string
): DocumentReference<FirestoreClickWrap> => {
  return doc(getColRef(firestore), clickWrapId)
}

export const getClickWrapList = (repository: FirebaseRepository) => {
  const ref = getColRef(repository.firestore)
  const q = query(ref)
  return modelListStream(repository, q, ClickWrap)
}

export const fetchClickWrap = async (
  repository: FirebaseRepository,
  { clickWrapId }: { clickWrapId: string }
) => {
  const ref = getDocRef(repository.firestore, clickWrapId)
  const doc = await getDocWithError(ref, 'FetchClickWrapError')
  return convertDocumentSnapshotToModel(repository, doc, ClickWrap)
}

export const getClickWrapListToAccept = (
  repository: FirebaseRepository,
  forRoles: ClickWrapRole[]
) => {
  const ref = getColRef(repository.firestore)
  const q = query(
    ref,
    where('clickWrapState', '==', ClickWrapState.active),
    where('clickWrapRoles', 'array-contains-any', forRoles)
  )
  return modelListStream(repository, q, ClickWrap)
}

export const createClickWrap = async (
  repository: FirebaseRepository,
  data: {
    clickWrapName: string
    clickWrapRoles: ClickWrapRole[]
    clickWrapDescription: string
    clickWrapUrl: string
    clickWrapDate: DateTime
  }
) => {
  return runTransactionWithError(
    repository.firestore,
    async (transaction) => {
      const time = serverTimestamp()

      const fields = {
        clickWrapName: data.clickWrapName,
        clickWrapRoles: data.clickWrapRoles,
        clickWrapDescription: data.clickWrapDescription,
        clickWrapUrl: data.clickWrapUrl,
        clickWrapState: ClickWrapState.active,
        clickWrapDate: Timestamp.fromDate(data.clickWrapDate.toJSDate()),
      }

      const ref = doc(getColRef(repository.firestore))
      transaction.set(ref, {
        ...fields,
        updatedAt: time,
      })
      const updateRef = doc(getUpdateColRef(ref))
      transaction.set(updateRef, {
        ...fields,
        userId: repository.uid,
        createdAt: time,
      })
      return ref.id
    },
    undefined,
    'CreateClickWrapError'
  )
}

export const updateClickWrap = async (
  repository: FirebaseRepository,
  params: {
    clickWrapId: string
  },
  {
    clickWrapName,
    clickWrapRoles,
    clickWrapDescription,
    clickWrapUrl,
    clickWrapState,
    clickWrapDate,
  }: {
    clickWrapName?: string
    clickWrapRoles?: ClickWrapRole[]
    clickWrapDescription?: string
    clickWrapUrl?: string
    clickWrapState?: number
    clickWrapDate?: DateTime
  }
) => {
  const updatedClickWrapDate = clickWrapDate
    ? Timestamp.fromDate(clickWrapDate.toJSDate())
    : undefined

  return runTransactionWithError(
    repository.firestore,
    async (transaction) => {
      const fields = {
        clickWrapName,
        clickWrapRoles,
        clickWrapDescription,
        clickWrapUrl,
        clickWrapState,
        clickWrapDate: updatedClickWrapDate,
      }
      const ref = getDocRef(repository.firestore, params.clickWrapId)
      transaction.update(ref, {
        ...fields,
        updatedAt: serverTimestamp(),
      })
      const updateRef = doc(collection(ref, 'update'))
      transaction.set(updateRef, {
        ...fields,
        userId: repository.uid,
        createdAt: serverTimestamp(),
      })
    },
    undefined,
    'ClickWrapUpdateError'
  )
}
